<template>
  <div id="smartTown" class="product-page">
    <!-- 标题区域 -->
    <h1 class="page-title">数字化开发管控平台</h1>

    <!-- 正文内容区域 -->
    <div class="page-content">
      <!-- 第1章 产品概述 -->
      <div class="text-section">
        <h2 class="chapter-title">一、产品概述</h2>
        <p class="content-paragraph">
          数字化开发管控平台融入 DataOps 理念，将敏捷开发方法运用于数据工程管理，提供一站式数据研发与数据治理能力，帮助用户快速构建从数据接入到数据分析的端到端数据体系。平台以项目空间为组织单元，覆盖需求、设计、开发、测试、发布与运维全过程，实现数据从需求到交付的全生命周期管理。
        </p>
        <p class="content-paragraph">
          平台由应用支撑平台、统一应用集成门户、通用中间件、管理控制台、运维监控、数据开发组件、数据资产管理组件与数据服务组件构成，支持国产化环境与主流大数据工具适配，提供租户化资源管理、可视化工作流编排、数据质量核验、数据标准管理、数据血缘分析与 API 服务能力，为业务系统建设与数据分析应用提供统一底座。
        </p>
        <!-- 核心能力 -->
        <div class="capability-grid">
          <div class="capability-card">
            <h3 class="capability-title">应用支撑与集成</h3>
            <ul class="capability-list"><li>统一认证授权</li><li>RBAC 权限</li><li>三员管理</li><li>单点登录</li><li>基础编码</li><li>服务组件</li><li>消息服务</li></ul>
          </div>
          <div class="capability-card">
            <h3 class="capability-title">数据开发与治理</h3>
            <ul class="capability-list"><li>数据集成</li><li>SQL 开发</li><li>工作流编排</li><li>任务调度</li><li>元数据管理</li><li>数据质量</li><li>数据标准</li></ul>
          </div>
          <div class="capability-card">
            <h3 class="capability-title">资产与服务</h3>
            <ul class="capability-list"><li>数据地图</li><li>血缘分析</li><li>资产分析</li><li>API 开发</li><li>服务订阅</li><li>调用统计</li><li>运维告警</li></ul>
          </div>
        </div>
      </div>

      <!-- 第2章 产品截图 -->
      <div class="text-section">
        <h2 class="chapter-title">二、产品截图</h2>
        <p class="shot-note">以下为系统功能示意，实际界面以项目交付成果为准。</p>
        <div class="shot-grid">
          <figure class="shot-card">
            <img
              src="@/assets/images/20250724/ruanjian/dg-dev.svg"
              alt="海南华德科技有限公司 数字化开发管控平台 - 数据开发 · 工作流编排与任务调度"
              loading="lazy"
            />
            <figcaption>
              <span class="shot-index">01</span>数据开发 · 工作流编排与任务调度
            </figcaption>
          </figure>
          <figure class="shot-card">
            <img
              src="@/assets/images/20250724/ruanjian/dg-asset.svg"
              alt="海南华德科技有限公司 数字化开发管控平台 - 数据资产 · 元数据血缘与质量治理"
              loading="lazy"
            />
            <figcaption>
              <span class="shot-index">02</span>数据资产 · 元数据血缘与质量治理
            </figcaption>
          </figure>
          <figure class="shot-card">
            <img
              src="@/assets/images/20250724/ruanjian/dg-console.svg"
              alt="海南华德科技有限公司 数字化开发管控平台 - 管理控制台 · 项目空间与工单审批"
              loading="lazy"
            />
            <figcaption>
              <span class="shot-index">03</span>管理控制台 · 项目空间与工单审批
            </figcaption>
          </figure>
          <figure class="shot-card">
            <img
              src="@/assets/images/20250724/ruanjian/dg-service.svg"
              alt="海南华德科技有限公司 数字化开发管控平台 - 数据服务 · API 开发与调用统计"
              loading="lazy"
            />
            <figcaption>
              <span class="shot-index">04</span>数据服务 · API 开发与调用统计
            </figcaption>
          </figure>
        </div>
      </div>

      <!-- 第3章 功能清单 -->
      <div class="text-section">
        <h2 class="chapter-title">三、功能清单</h2>
        <div class="func-table-wrap">
        <table class="func-table" border="1" cellpadding="10" cellspacing="0" width="100%">
        <thead>
        <tr>
        <th style="background-color: #f0f0f0; vertical-align: top; width: 15%;">功能模块</th>
        <th style="background-color: #f0f0f0; vertical-align: top; width: 20%;">子模块</th>
        <th style="background-color: #f0f0f0; vertical-align: top; width: 65%;">功能说明</th>
        </tr>
        </thead>
        <tbody>
        <!-- 应用支撑平台 -->
        <tr>
        <td rowspan="1" style="vertical-align: top;">应用支撑平台</td>
        <td style="vertical-align: top;">基础功能封装</td>
        <td style="vertical-align: top;">
        1. 封装用户组织身份、权限控制、工作流、表单等基础功能，支持上层应用系统开发；<br>
        2. 基于面向服务架构设计，提供统一的表单、流程、事项、办件、通知和用户中心；<br>
        3. 适配国产环境，兼容hadoop、hive、spark等大数据工具。
        </td>
        </tr>

        <!-- 统一应用集成门户 -->
        <tr>
        <td rowspan="1" style="vertical-align: top;">统一应用集成门户</td>
        <td style="vertical-align: top;">系统集成与访问</td>
        <td style="vertical-align: top;">
        1. 集成松耦合信息系统，实现“一个系统”融合，支持VPN、电子政务外网访问；<br>
        2. 单点登录：一次登录即可访问所有集成应用，支持页面集成；<br>
        3. 用户/权限集成：统一用户管理，按角色分配权限，限制访问范围。
        </td>
        </tr>

        <!-- 通用中间件 -->
        <tr>
        <td rowspan="3" style="vertical-align: top;">通用中间件</td>
        <td style="vertical-align: top;">统一用户认证与授权组件</td>
        <td style="vertical-align: top;">
        1. 包含用户管理（新增、维护、权限分配）、系统管理（业务系统维护）、功能/数据/隐私权限管理；<br>
        2. 支持RBAC模型，实现“三员管理”（系统管理员、业务管理员、日志管理员）；<br>
        3. 与中国疾控中心用户系统同步数据，提供认证服务和统计分析功能。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">统一基础编码组件</td>
        <td style="vertical-align: top;">
        1. 支撑所有业务系统的公共管理模块，复用省政务云编码系统；<br>
        2. 功能包括编码维护（行政区划、机构、疾病种类等增删改查）、审核、发布及查询浏览；<br>
        3. 所有操作均有日志监控，确保编码规范和一致性。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">统一服务组件</td>
        <td style="vertical-align: top;">
        1. 服务类别管理（分类维护）、服务创建与发布（状态管理）、服务订阅（权限校验）；<br>
        2. 服务调用统计（多维度分析）、日志审计（运行监控）、统一消息服务（多渠道提醒）；<br>
        3. 支持疫情预警等场景的消息推送（如短信提醒）。
        </td>
        </tr>

        <!-- 管理控制台 -->
        <tr>
        <td rowspan="7" style="vertical-align: top;">管理控制台</td>
        <td style="vertical-align: top;">项目空间</td>
        <td style="vertical-align: top;">
        1. 以项目为单元的开发协作模式，资源租户化，支持多角色协同；<br>
        2. 功能包括创建项目（隶属机构，共享资源）、项目列表（展示参与项目及管理入口）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">项目配置</td>
        <td style="vertical-align: top;">
        1. 基础信息配置（名称、描述、租户）、成员管理（角色分配）、资源配置（环境资源申请）；<br>
        2. 数据源配置（支持主流数据库及分库分表场景，引用统一数据源）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">管理中心</td>
        <td style="vertical-align: top;">
        1. 项目/机构资源管理：展示资源申请状态（申请中、通过、不通过），支持撤销、变更、重新申请；<br>
        2. 我的工单：待办工单（审批）、工单跟踪（流转中）、工单查询（历史记录）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">消息中心</td>
        <td style="vertical-align: top;">
        1. 系统公告：展示接收的公告，支持搜索和详情查看；<br>
        2. 系统通知：包括项目变更、工单审批等消息，支持实时推送、快速操作（如跳转、下载）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">系统管理</td>
        <td style="vertical-align: top;">
        1. 环境管理（开发/测试/生产环境配置）、租户管理（资源分配）、统一数据源管理；<br>
        2. 组织/角色/菜单/权限管理（用户及访问控制）、审批流程管理（自定义流程节点）；<br>
        3. 公告管理（推送策略配置）、系统操作日志（筛选查询）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">规范设计</td>
        <td style="vertical-align: top;">
        1. 主题域设计（增删改查、层级配置，最多三层）；<br>
        2. 通用字段类型设计（定义逻辑字段类型，物理化时映射为数据库类型）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">控制台首页</td>
        <td style="vertical-align: top;">
        1. 集成各模块入口，展示项目数、待审批工单、资源使用情况；<br>
        2. 支持快速创建项目，查看参与项目动态和机构资源。
        </td>
        </tr>

        <!-- 运维监控 -->
        <tr>
        <td rowspan="2" style="vertical-align: top;">运维监控</td>
        <td style="vertical-align: top;">首页与工作流监控</td>
        <td style="vertical-align: top;">
        1. 全局概览：工作流/任务运行统计（时间范围筛选、耗时排行）；<br>
        2. 工作流监控：列表/DAG视图展示，支持暂停/恢复调度、手动运行、查看历史及日志。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">项目管理与告警</td>
        <td style="vertical-align: top;">
        1. 项目管理：展示已上线项目及概览（工作流/任务统计）；<br>
        2. 任务地图：搜索工作流/实例及详情；<br>
        3. 告警管理：多渠道推送告警（钉钉、短信等），支持重发；<br>
        4. 分布式调度引擎：支持DAG任务调度、终止、重跑及日志查看。
        </td>
        </tr>

        <!-- 数据开发组件 -->
        <tr>
        <td rowspan="3" style="vertical-align: top;">数据开发组件</td>
        <td style="vertical-align: top;">表管理与批量数据开发</td>
        <td style="vertical-align: top;">
        1. 表管理：底座表（可视化/DDL建表、版本管理）、沙箱表（模拟生产数据用于开发测试）；<br>
        2. 任务开发：数据集成（多数据源同步）、SQL脚本开发（IDE体验、版本管理）；<br>
        3. 工作流开发：拖拽式编排、依赖配置、调度策略（定时/手动触发）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">元数据与任务发布</td>
        <td style="vertical-align: top;">
        1. 元数据管理：查看与编辑库、表、字段信息；<br>
        2. 任务发布：发布包管理（上线/下线打包）、测试（冒烟测试及报告）、部署至生产环境；<br>
        3. 支持网络可达/不可达场景（导出导入发布包）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">辅助功能</td>
        <td style="vertical-align: top;">
        1. 函数管理（上传.jar资源）、数据探索（即席查询）、脚本模板管理（规范开发）；<br>
        2. 回收站（恢复/彻底删除节点）。
        </td>
        </tr>

        <!-- 数据资产管理组件 -->
        <tr>
        <td rowspan="4" style="vertical-align: top;">数据资产管理组件</td>
        <td style="vertical-align: top;">数据地图与资产分析</td>
        <td style="vertical-align: top;">
        1. 数据地图：资产搜索（多条件检索）、资产详情（表信息、血缘、权限申请）、数据血缘（表/字段级图谱）；<br>
        2. 资产分析：概览（表数量、热度等）、报表（按主题域统计）、消费统计（API调用、数据交付）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">元数据管理</td>
        <td style="vertical-align: top;">
        1. 元数据编辑（检索与完善属性）、元模型管理（定义元数据类型）；<br>
        2. 目录树管理（系统/自定义目录，资产挂载）、批量维护（导出编辑后导入）；<br>
        3. 标签管理（元数据标签增删改查）、码值管理（维护内部代码键值）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">数据质量管理</td>
        <td style="vertical-align: top;">
        1. 质量概览（主题域核验统计）、核验管理（规则配置、调度策略）；<br>
        2. 任务实例（查看运行状态及日志）、模板规则（预置核验模板如非空、正则）；<br>
        3. 异常告警、问题管理（跟踪与闭环）、质量监控（趋势分析、报告）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">数据标准管理</td>
        <td style="vertical-align: top;">
        1. 资产管理员：词根库、码值、码值映射、元数据的增删改查；<br>
        2. 普通用户：查看和导出词根库、码值、映射及元数据。
        </td>
        </tr>

        <!-- 数据服务组件 -->
        <tr>
        <td rowspan="2" style="vertical-align: top;">数据服务组件</td>
        <td style="vertical-align: top;">数据服务开发</td>
        <td style="vertical-align: top;">
        1. API开发：DataToApi（SQL转换为API，多数据源支持）、UrlToApi（注册已有API）；<br>
        2. 项目API管理：我的发布（检索与操作API）、我的申请/审核（工单处理）、API统计（调用分析）。
        </td>
        </tr>
        <tr>
        <td style="vertical-align: top;">数据服务管理</td>
        <td style="vertical-align: top;">
        1. 服务概览（调用统计、服务状态）、服务管理（检索、操作、绑定APP）；<br>
        2. APP管理（创建、密钥更新）、审核管理（工单审批）；<br>
        3. 告警管理（规则配置与记录）、资源管理（k8s集群资源监控）；<br>
        4. 日志管理（API调用日志检索与详情查看）。
        </td>
        </tr>
        </tbody>
        </table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: "smartTown",
  data() {
    return {};
  },
};
</script>
<style scoped>
/* 全局容器样式 */
.product-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: "Poppins", "Microsoft YaHei", "PingFang SC", sans-serif;
  background-color: var(--light-bg);
  min-height: 100vh;
}

/* 标题样式 */
.page-title {
  font-weight: 700;
  font-size: 32px;
  color: var(--text-color);
  text-align: center;
  margin: 0 auto 50px;
  padding-bottom: 15px;
  border-bottom: 3px solid var(--primary-color1);
  display: inline-block;
  width: 100%;
  letter-spacing: 1px;
}

/* 内容容器 */
.page-content {
  background-color: var(--white);
  padding: 50px 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* 章节区域 */
.text-section {
  margin: 20px 0 60px;
}
.text-section:last-child {
  margin-bottom: 20px;
}

/* 章节标题 */
.chapter-title {
  font-weight: 700;
  font-size: 20px;
  color: var(--text-color);
  margin: 10px 0 25px;
  padding-left: 10px;
  border-left: 4px solid var(--primary-color1);
  line-height: 1.4;
}

/* 正文段落 */
.content-paragraph {
  line-height: 1.8;
  font-weight: 400;
  color: var(--text-color2);
  margin: 8px 0 22px;
  text-indent: 2em;
  font-size: 16px;
  text-align: justify;
}
.content-paragraph strong {
  color: var(--primary-color1);
  font-weight: 600;
}

/* 核心能力卡片 */
.capability-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 30px;
}
.capability-card {
  background-color: var(--light-bg);
  border-radius: 10px;
  padding: 22px 20px;
  border-top: 3px solid var(--primary-color1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.capability-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}
.capability-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-color);
  margin: 0 0 14px;
}
.capability-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.capability-list li {
  display: inline-block;
  background-color: var(--white);
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 4px 12px;
  margin: 0 6px 8px 0;
  font-size: 13px;
  color: var(--text-color2);
  transition: all 0.3s ease;
}
.capability-list li:hover {
  border-color: rgba(230, 0, 76, 0.35);
  color: var(--primary-color1);
}

/* 产品截图 */
.shot-note {
  font-size: 13px;
  color: var(--text-color3);
  margin: -10px 0 22px;
}
.shot-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}
.shot-card {
  margin: 0;
  background: var(--white);
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}
.shot-card:hover {
  transform: translateY(-4px);
  border-color: rgba(230, 0, 76, 0.25);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06), 0 10px 28px rgba(0, 0, 0, 0.08);
}
.shot-card img {
  display: block;
  width: 100%;
  height: auto;
}
.shot-card figcaption {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  font-size: 14px;
  color: var(--text-color);
  border-top: 1px solid #f2f2f2;
  background: var(--white);
}
.shot-index {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-color1);
  background: rgba(230, 0, 76, 0.08);
  border-radius: 4px;
  padding: 2px 7px;
}

/* 响应式 */
@media (max-width: 991px) {
  .capability-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 767px) {
  .product-page {
    padding: 24px 14px;
  }
  .page-title {
    font-size: 24px;
    margin-bottom: 30px;
  }
  .page-content {
    padding: 28px 18px;
  }
  .capability-grid,
  .shot-grid {
    grid-template-columns: 1fr;
  }
  .content-paragraph {
    font-size: 15px;
  }
}

/* 功能清单表格 */
.func-table-wrap {
  overflow-x: auto;
}
.func-table {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0 0;
  font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;
  font-size: 14px;
  line-height: 1.7;
  color: var(--text-color2);
}
.func-table th,
.func-table td {
  border: 1px solid #e6e8eb;
  padding: 10px 12px;
  text-align: left;
  vertical-align: top;
  font-weight: 400;
}
.func-table th {
  background-color: #f5f7fa;
  color: var(--text-color);
  font-weight: 700;
}
.func-table tr:nth-child(even) td {
  background-color: #fafbfc;
}
@media (max-width: 767px) {
  .func-table {
    font-size: 13px;
  }
  .func-table th,
  .func-table td {
    padding: 8px 10px;
  }
}
</style>





// WEBPACK FOOTER //
// src/view/softwareWs5.vue